import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import ApiService from '../../services/api';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AdminPanelSettings as AdminIcon,
  PersonOutline as UserIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

// Import our custom components
import PageHeader from '../layout/PageHeader';
import ConfirmDialog from '../common/ConfirmDialog';
import SearchBar from '../common/SearchBar';
import EmptyState from '../common/EmptyState';
import Loading from '../layout/Loading';

const AdminUsers = () => {
  const { token } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token, page, rowsPerPage]);

  const fetchUsers = async (search = searchTerm) => {
    try {
      setLoading(true);
      
      // Build query string
      const queryParams = new URLSearchParams({
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage
      });
      
      if (search) {
        queryParams.append('search', search);
      }
      
      // Fetch users
      const response = await ApiService.get(`/api/admin/users?${queryParams.toString()}`, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users || []);
        setTotalUsers(data.pagination.total || 0);
      } else {
        toast.error(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(0);
    fetchUsers(value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await ApiService.get(`/api/admin/users/${userId}`, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedUser(data.user);
        setUserDialogOpen(true);
      } else {
        toast.error(data.message || 'Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const openConfirmDialog = (action, user, isRole = false) => {
    setSelectedUser(user);
    setConfirmAction(action);
    
    if (action === 'toggleRole') {
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      setConfirmTitle(`Change User Role`);
      setConfirmMessage(`Are you sure you want to change ${user.name}'s role from ${user.role} to ${newRole}?`);
    } else if (action === 'toggleActive') {
      const actionText = user.isActive ? 'deactivate' : 'activate';
      setConfirmTitle(`${user.isActive ? 'Deactivate' : 'Activate'} User`);
      setConfirmMessage(`Are you sure you want to ${actionText} ${user.name}'s account?`);
    }
    
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    setActionLoading(true);
    
    if (confirmAction === 'toggleRole') {
      await handleToggleRole();
    } else if (confirmAction === 'toggleActive') {
      await handleToggleActive();
    }
    
    setActionLoading(false);
    setConfirmDialogOpen(false);
  };

  const handleToggleRole = async () => {
    if (!selectedUser) return;
    
    const userId = selectedUser._id;
    const currentRole = selectedUser.role;
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    
    try {
      const response = await ApiService.put(`/api/admin/users/${userId}/role`, { role: newRole }, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`User role updated to ${newRole}`);
        fetchUsers();
        
        // Update selected user if dialog is open
        if (userDialogOpen) {
          setSelectedUser({
            ...selectedUser,
            role: newRole
          });
        }
      } else {
        toast.error(data.message || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleToggleActive = async () => {
    if (!selectedUser) return;
    
    const userId = selectedUser._id;
    const isActive = selectedUser.isActive;
    const newStatus = !isActive;
    
    try {
      const response = await ApiService.put(`/api/admin/users/${userId}/status`, { isActive: newStatus }, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchUsers();
        
        // Update selected user if dialog is open
        if (userDialogOpen) {
          setSelectedUser({
            ...selectedUser,
            isActive: newStatus
          });
        }
      } else {
        toast.error(data.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} user`);
      }
    } catch (error) {
      console.error(`Error toggling user status:`, error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && users.length === 0) {
    return <Loading message="Loading users..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Manage Users"
        subtitle="View and manage user accounts"
        breadcrumbs={[
          { label: 'Admin', link: '/admin' },
          { label: 'Users', link: '/admin/users' }
        ]}
      />
      
      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </Box>
      
      {/* Users Table */}
      {users.length > 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Joined Date</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: user?.role === 'admin' ? 'primary.main' : 'secondary.main' 
                          }}
                        >
                          {user?.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">{user.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{user.email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.role === 'admin' ? <AdminIcon fontSize="small" /> : <UserIcon fontSize="small" />}
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                        variant={user.role === 'admin' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.isActive ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                        variant={user.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewUser(user._id)}
                          color="primary"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openConfirmDialog('toggleRole', user)}
                          color="secondary"
                        >
                          {user.role === 'admin' ? <UserIcon fontSize="small" /> : <AdminIcon fontSize="small" />}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openConfirmDialog('toggleActive', user)}
                          color={user.isActive ? 'error' : 'success'}
                        >
                          {user.isActive ? <InactiveIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <EmptyState
          title="No users found"
          description={searchTerm ? "Try adjusting your search terms" : "There are no users registered in the system yet"}
          icon={<PersonIcon />}
        />
      )}
      
      {/* User Details Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        {selectedUser && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">User Details</Typography>
                <IconButton onClick={() => setUserDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mr: 2,
                    bgcolor: selectedUser.role === 'admin' ? 'primary.main' : 'secondary.main'
                  }}
                >
                  {selectedUser.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUser.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardHeader title="Account Information" />
                    <Divider />
                    <CardContent>
                      <List dense disablePadding>
                        <ListItem>
                          <ListItemIcon>
                            <CalendarIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Joined Date" 
                            secondary={formatDate(selectedUser.createdAt)} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <SecurityIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Role" 
                            secondary={
                              <Chip
                                size="small"
                                label={selectedUser.role}
                                color={selectedUser.role === 'admin' ? 'primary' : 'default'}
                              />
                            } 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            {selectedUser.isActive ? 
                              <ActiveIcon fontSize="small" color="success" /> : 
                              <InactiveIcon fontSize="small" color="error" />
                            }
                          </ListItemIcon>
                          <ListItemText 
                            primary="Status" 
                            secondary={
                              <Chip
                                size="small"
                                label={selectedUser.isActive ? 'Active' : 'Inactive'}
                                color={selectedUser.isActive ? 'success' : 'error'}
                              />
                            } 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardHeader title="Profile Information" />
                    <Divider />
                    <CardContent>
                      <List dense disablePadding>
                        <ListItem>
                          <ListItemText 
                            primary="Age" 
                            secondary={selectedUser.profile?.age || 'Not specified'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Height" 
                            secondary={selectedUser.profile?.height ? `${selectedUser.profile.height} cm` : 'Not specified'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Weight" 
                            secondary={selectedUser.profile?.weight ? `${selectedUser.profile.weight} kg` : 'Not specified'} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Goals" 
                            secondary={selectedUser.profile?.goals || 'Not specified'} 
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => openConfirmDialog('toggleRole', selectedUser)} 
                color="secondary"
                startIcon={selectedUser.role === 'admin' ? <UserIcon /> : <AdminIcon />}
              >
                Change to {selectedUser.role === 'admin' ? 'User' : 'Admin'}
              </Button>
              <Button 
                onClick={() => openConfirmDialog('toggleActive', selectedUser)} 
                color={selectedUser.isActive ? 'error' : 'success'}
                startIcon={selectedUser.isActive ? <InactiveIcon /> : <ActiveIcon />}
              >
                {selectedUser.isActive ? 'Deactivate' : 'Activate'} User
              </Button>
              <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialogOpen(false)}
        loading={actionLoading}
        type={confirmAction === 'toggleActive' && selectedUser?.isActive ? 'warning' : 'info'}
      />
    </Box>
  );
};

export default AdminUsers;
